import { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { broadcastAdminNotification } from '../api/adminNotifications';
import { listApps } from '../api/appsApi';
import { getApiBaseUrl } from '../utils/apiClient';
import { useAppContext } from '../context/AppContext';

function formatBroadcastError(err) {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const msg =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message;
    if (!err.response) {
      return `${msg} (no response — check CORS, API URL, or network). API base: ${getApiBaseUrl()}`;
    }
    return `${msg}${status ? ` [HTTP ${status}]` : ''}`;
  }
  return err?.message || String(err);
}

export function NotificationsPanel() {
  const { firebaseProjectFilter, setFirebaseProjectFilter, firebaseProjectIds } = useAppContext();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dataJson, setDataJson] = useState('');
  const [sending, setSending] = useState(false);

  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [selectedSlugs, setSelectedSlugs] = useState([]);

  useEffect(() => {
    listApps()
      .then((res) => setApps(res?.apps || []))
      .catch(() => setApps([]))
      .finally(() => setAppsLoading(false));
  }, []);

  // When the global Firebase project filter changes, clear the app selection
  // to avoid hidden cross-project state
  useEffect(() => {
    setSelectedSlugs([]);
  }, [firebaseProjectFilter]);

  // Apps shown in the checklist — driven by the sidebar Firebase project filter
  const visibleApps = firebaseProjectFilter
    ? apps.filter((a) => a.firebaseProjectId === firebaseProjectFilter)
    : apps;

  const allVisibleSelected =
    visibleApps.length > 0 && visibleApps.every((a) => selectedSlugs.includes(a.slug));

  function toggleAll() {
    if (allVisibleSelected) {
      const visibleSet = new Set(visibleApps.map((a) => a.slug));
      setSelectedSlugs((prev) => prev.filter((s) => !visibleSet.has(s)));
    } else {
      const visibleSlugs = visibleApps.map((a) => a.slug);
      setSelectedSlugs((prev) => [...new Set([...prev, ...visibleSlugs])]);
    }
  }

  function toggleSlug(slug) {
    setSelectedSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  }

  function targetLabel() {
    if (!selectedSlugs.length) return firebaseProjectFilter ? `All in ${firebaseProjectFilter}` : 'All Apps';
    if (selectedSlugs.length === 1) {
      const app = apps.find((a) => a.slug === selectedSlugs[0]);
      return app?.name || selectedSlugs[0];
    }
    return `${selectedSlugs.length} apps`;
  }

  // Cross-project conflict — safety net when "All Projects" is active
  const selectedProjectIds = [
    ...new Set(
      selectedSlugs
        .map((slug) => apps.find((a) => a.slug === slug)?.firebaseProjectId)
        .filter(Boolean)
    ),
  ];
  const hasCrossProjectConflict = selectedProjectIds.length > 1;

  const handleSend = async (e) => {
    e.preventDefault();

    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    if (!trimmedTitle || !trimmedBody) {
      toast.error('Title and body are required.');
      return;
    }

    let extraData = {};
    if (dataJson.trim()) {
      try {
        const parsed = JSON.parse(dataJson);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          toast.error('Data payload must be a JSON object, e.g. {"screen":"promos"}');
          return;
        }
        extraData = parsed;
      } catch {
        toast.error('Invalid JSON in data payload.');
        return;
      }
    }

    const label = targetLabel();
    const toastId = toast.loading(`Sending to ${label}…`);
    setSending(true);
    try {
      const res = await broadcastAdminNotification({
        title: trimmedTitle,
        body: trimmedBody,
        data: extraData,
        appSlugs: selectedSlugs,
      });

      if (!res?.success) {
        toast.error(res?.message || 'Broadcast failed (server returned success: false)', { id: toastId });
        return;
      }

      const total = res.totalTokens ?? 0;
      const ok = res.successCount ?? 0;
      const fail = res.failureCount ?? 0;

      if (total === 0) {
        toast(
          res.message ||
            `No device tokens registered yet for ${label}. Open the mobile app to register FCM.`,
          {
            id: toastId,
            icon: 'ℹ️',
            duration: 8500,
            style: { borderLeft: '3px solid #38bdf8' },
          },
        );
      } else if (fail > 0) {
        toast(
          `Sent to ${total} device(s). Firebase: ${ok} success, ${fail} failure(s) — check invalid FCM tokens.`,
          {
            id: toastId,
            icon: '⚠️',
            duration: 8000,
            style: { borderLeft: '3px solid #fbbf24' },
          },
        );
      } else {
        toast.success(
          `Delivered to ${label} — ${total} device(s), ${ok} success, ${fail} failed.`,
          { id: toastId, duration: 6000 },
        );
      }

      setTitle('');
      setBody('');
      setDataJson('');
    } catch (err) {
      console.error('[admin-notifications] broadcast failed', err);
      toast.error(formatBroadcastError(err), { id: toastId, duration: 9000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card h-full">
      <div className="card-header">
        <div>
          <h2 className="card-title">Broadcast notification</h2>
          <p className="card-subtitle">
            Target:{' '}
            {selectedSlugs.length === 0 ? (
              <span className="text-amber-400 font-medium">{targetLabel()}</span>
            ) : (
              <span className="text-sage-light font-medium">{targetLabel()}</span>
            )}
            {' '}— check apps below to narrow the target.
          </p>
        </div>
      </div>

      {/* Active Firebase project filter context (sidebar handles the dropdown) */}
      {firebaseProjectFilter && (
        <div className="flex items-center justify-between mb-3 px-3 py-2 rounded-lg bg-sage-light/8 border border-sage-light/20 text-[11px]">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-sage-light flex-shrink-0" />
            <span className="text-gray-400">Firebase project:</span>
            <span className="font-mono text-sage-light font-medium">{firebaseProjectFilter}</span>
          </div>
          <button
            type="button"
            onClick={() => setFirebaseProjectFilter(null)}
            className="text-gray-500 hover:text-white transition-colors ml-3 text-[10px]"
          >
            Clear
          </button>
        </div>
      )}

      {/* App checklist */}
      <div className="mb-4">
        <div className="label mb-2">Target apps</div>

        {appsLoading ? (
          <p className="text-xs text-gray-400">Loading apps…</p>
        ) : apps.length === 0 ? (
          <p className="text-xs text-gray-400">No registered apps found.</p>
        ) : visibleApps.length === 0 ? (
          <p className="text-xs text-gray-500 py-1">
            No apps in <span className="font-mono">{firebaseProjectFilter}</span>.
          </p>
        ) : (
          <div className="border border-white/10 rounded-lg overflow-hidden">
            {/* Select all — scoped to visible apps */}
            <label className="flex items-center gap-2 px-3 py-2 bg-white/5 border-b border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleAll}
                className="w-4 h-4 accent-sage-light rounded"
              />
              <span className="text-xs font-semibold text-gray-300">
                {allVisibleSelected ? 'Deselect all' : 'Select all'}
                {firebaseProjectFilter && (
                  <span className="text-gray-500 font-normal ml-1">
                    — in {firebaseProjectFilter}
                  </span>
                )}
              </span>
            </label>

            {/* Individual app rows */}
            <div className="max-h-52 overflow-y-auto divide-y divide-white/5">
              {visibleApps.map((app) => {
                const isChecked = selectedSlugs.includes(app.slug);
                return (
                  <label
                    key={app.slug}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                      isChecked ? 'bg-sage-light/5' : 'hover:bg-white/5'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleSlug(app.slug)}
                      className="w-4 h-4 accent-sage-light rounded flex-shrink-0"
                    />
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: app.color || '#6b7280' }}
                    />
                    <span className="text-sm text-gray-200 font-medium flex-1 truncate min-w-0">
                      {app.name}
                    </span>
                    <span className="text-[10px] text-gray-500 font-mono truncate max-w-[140px] flex-shrink-0">
                      {app.packageName}
                    </span>
                    {/* Show Firebase badge only when "All projects" is active */}
                    {!firebaseProjectFilter && app.firebaseProjectId && (
                      <span
                        className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-gray-500 truncate max-w-[110px] flex-shrink-0"
                        title={`Firebase: ${app.firebaseProjectId}`}
                      >
                        {app.firebaseProjectId}
                      </span>
                    )}
                    {!app.isActive && (
                      <span className="text-[10px] text-red-400 flex-shrink-0">inactive</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Cross-project conflict warning */}
        {hasCrossProjectConflict && (
          <div className="mt-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/25 text-xs text-red-400 leading-relaxed">
            <span className="font-semibold">Cross-project conflict:</span> selected apps span{' '}
            {selectedProjectIds.join(' and ')}. Use the project filter above to send to one project at a time.
          </div>
        )}

        {/* Selection summary */}
        {selectedSlugs.length > 0 && !hasCrossProjectConflict && (
          <p className="text-[11px] text-gray-500 mt-1.5">
            {selectedSlugs.length} app{selectedSlugs.length !== 1 ? 's' : ''} selected
            {selectedProjectIds.length === 1 && (
              <span className="font-mono text-gray-600 ml-1">· {selectedProjectIds[0]}</span>
            )}
            <button
              type="button"
              onClick={() => setSelectedSlugs([])}
              className="ml-2 text-gray-600 hover:text-gray-400 underline"
            >
              clear
            </button>
          </p>
        )}
      </div>

      {/* Notification form */}
      <form onSubmit={handleSend} className="space-y-3">
        <div>
          <label className="label" htmlFor="notif-title">Title</label>
          <input
            id="notif-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={60}
            placeholder="New feature update"
          />
        </div>
        <div>
          <label className="label" htmlFor="notif-body">Body</label>
          <textarea
            id="notif-body"
            className="input min-h-[72px] resize-none"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={160}
            placeholder="We shipped improvements for wifi quality and speed."
          />
        </div>
        <div>
          <label className="label" htmlFor="notif-data">
            Data payload (optional JSON object)
          </label>
          <textarea
            id="notif-data"
            className="input min-h-[56px] resize-none font-mono text-[11px]"
            value={dataJson}
            onChange={(e) => setDataJson(e.target.value)}
            placeholder='{"route":"promos"}'
          />
        </div>
        <button
          type="submit"
          disabled={sending || hasCrossProjectConflict}
          className="btn-primary w-full mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? 'Sending…' : `Send to ${targetLabel()}`}
        </button>
      </form>
    </div>
  );
}
